import { useEffect, useRef } from 'react'
import { useReceivedOrders } from '../contexts/ReceivedOrdersContext'
import { useTerminal } from '../contexts/TerminalContext'

const CHIME_STORAGE_PREFIX = 'pos.chimeEnabled.'

export const getChimeStorageKey = (terminalId: string | null): string =>
  `${CHIME_STORAGE_PREFIX}${terminalId && terminalId.length > 0 ? terminalId : '__default__'}`

export const isChimeEnabled = (terminalId: string | null): boolean => {
  if (typeof window === 'undefined') return false
  const raw = window.localStorage.getItem(getChimeStorageKey(terminalId))
  // Default: ON. Only disabled when explicitly set to "false".
  return raw !== 'false'
}

export const setChimeEnabled = (terminalId: string | null, enabled: boolean): void => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(getChimeStorageKey(terminalId), enabled ? 'true' : 'false')
}

const playChime = () => {
  try {
    const AudioCtx =
      (window as any).AudioContext || (window as any).webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    // 150ms with fast fade-out to avoid a click on stop.
    const now = ctx.currentTime
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.2, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.16)
    osc.onended = () => ctx.close()
  } catch {
    // Best-effort. If the browser blocks AudioContext (no user interaction yet),
    // we silently skip — the visual banner is the primary signal.
  }
}

/**
 * Mount once near the app root. Subscribes to `chimeTick` increments coming
 * from ReceivedOrdersContext and plays a single soft tone when the pending
 * count strictly increases, gated by the per-terminal localStorage flag.
 */
export const useNewOrderChime = () => {
  const { chimeTick } = useReceivedOrders()
  const { selectedTerminal } = useTerminal()
  const lastTickRef = useRef(chimeTick)

  useEffect(() => {
    if (chimeTick === lastTickRef.current) return
    lastTickRef.current = chimeTick
    if (!isChimeEnabled(selectedTerminal)) return
    playChime()
  }, [chimeTick, selectedTerminal])
}
